import { Injectable, OnDestroy } from '@angular/core';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { SocketService } from './socket.service';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { environment } from '@env/environment';
import { logger } from '@shared/utils/functions.utils';
import { Socket } from 'socket.io-client';

/**
 * Servicio para manejar eventos de chat vía WebSocket
 * Implementa OnDestroy para limpieza automática de recursos
 */
@Injectable({
  providedIn: 'root',
})
export class MicaChatListenerService implements OnDestroy {
  constructor(private socketService: SocketService) {}
  private readonly namespace = '/mica-chat';
  private sucursalIdActual: number | null = null;

  // Subject privado (no exponer públicamente)
  private readonly eventNotificationSubject = new Subject<IEventNotification>();

  // Observable público readonly (inmutable desde fuera)
  readonly events$: Observable<IEventNotification> = this.eventNotificationSubject.asObservable();

  /**
   * Filtra eventos por tipo específico
   * @param eventType Tipo de evento a filtrar
   */
  onEvent<T = unknown>(eventType: EVENT_GATEWAY): Observable<IEventNotification<T>> {
    return this.events$.pipe(
      filter((notification) => notification.event === eventType),
    ) as Observable<IEventNotification<T>>;
  }

  /**
   * Obtiene solo los datos del evento, sin metadata
   * @param eventType Tipo de evento
   */
  getData<T = unknown>(eventType: EVENT_GATEWAY): Observable<T> {
    return this.onEvent<T>(eventType).pipe(map((notification) => notification.data));
  }

  /**
   * Filtra eventos por sucursal
   * @param sucursalId ID de la sucursal
   */
  onSucursal(sucursalId: number): Observable<IEventNotification> {
    return this.events$.pipe(filter((notification) => notification.sucursalId === sucursalId));
  }

  /**
   * Detiene el listener y limpia recursos
   */
  stop(sucursal_id: number): void {
    const socket = this.socketService.obtenerSocket(sucursal_id, this.namespace);
    if (!socket) return;

    // Desuscribir dinámicamente antes de desconectar
    this.socketService.desuscribirSucursal(sucursal_id, this.namespace);
    this.sucursalIdActual = null;

    // Remover listeners para evitar fugas de memoria
    this.limpiarListeners(socket);

    this.socketService.desconectarSocketEspecifico(sucursal_id, this.namespace);
  }

  /**
   * Inicia el listener para una sucursal
   */
  start(sucursal_id: number): void {
    const socket = this.socketService.conectarSucursal(sucursal_id, this.namespace, environment.apiMicaChatUrl);
    // Log adicional de debug (solo en desarrollo)
    if (!environment.production) {
      logger('[MicaChatListener] Estado:', {
        connected: socket?.connected,
        namespace: this.namespace,
        id: socket?.id,
      });
    }

    if (!socket) {
      console.error('[MicaChatListener] No se pudo obtener la instancia del socket');
      return;
    }

    if (!environment.production) {
      logger('[MicaChatListener] Inicializando listeners...');
    }

    this.limpiarListeners(socket);

    // Suscribirse dinámicamente a la sucursal (si el socket ya está conectado)
    // Si aún no está conectado, esperar al evento connect para suscribirse
    if (socket.connected) {
      this.socketService.suscribirSucursal(sucursal_id, this.namespace);
      this.sucursalIdActual = sucursal_id;
    }

    // Evento de conexión exitosa
    socket.on(EVENT_GATEWAY.CONNECT, () => {
      if (!environment.production) {
        logger('[MicaChatListener] ✅ Conectado al servidor WebSocket');
      }
      // Suscribir a la sucursal al conectar
      this.socketService.suscribirSucursal(sucursal_id, this.namespace);
      this.sucursalIdActual = sucursal_id;
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.CONNECT,
        data: null,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Evento de desconexión
    socket.on(EVENT_GATEWAY.DISCONNECT, (reason: string) => {
      logger('[MicaChatListener] ❌ Desconectado del servidor:', reason);
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.DISCONNECT,
        data: reason,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Listeners de eventos de chat
    socket.on(EVENT_GATEWAY.MICA_CHAT_CHAT_NUEVO, (data) => {
      if (!environment.production) {
        logger('[MicaChatListener] 🆕 Chat nuevo:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.MICA_CHAT_CHAT_NUEVO,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });
    socket.on(EVENT_GATEWAY.MICA_CHAT_CHAT_ACTUALIZADO, (data) => {
      if (!environment.production) {
        logger('[MicaChatListener] 📋 Chat actualizado:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.MICA_CHAT_CHAT_ACTUALIZADO,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.MICA_CHAT_MENSAJE_NUEVO, (data) => {
      if (!environment.production) {
        logger('[MicaChatListener] 📝 Mensaje nuevo en el chat:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.MICA_CHAT_MENSAJE_NUEVO,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.MICA_CHAT_MENSAJE_ACTUALIZADO, (data) => {
      if (!environment.production) {
        logger('[MicaChatListener] 📍 Mensaje actualizado en el chat:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.MICA_CHAT_MENSAJE_ACTUALIZADO,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    socket.on(EVENT_GATEWAY.MICA_CHAT_CONEXION_ACTUALIZADA, (data) => {
      if (!environment.production) {
        logger('[MicaChatListener] 🔌 Conexión actualizada:', data);
      }
      this.eventNotificationSubject.next({
        event: EVENT_GATEWAY.MICA_CHAT_CONEXION_ACTUALIZADA,
        data,
        sucursalId: sucursal_id,
        timestamp: new Date(),
      });
    });

    // Log del estado de conexión inicial (solo en desarrollo)
    if (!environment.production) {
      logger(
        `[MicaChatListener] Estado inicial: ${socket.connected ? '✅ Conectado' : '⏳ Conectando...'}`,
      );
    }
  }

  /**
   * Limpieza automática cuando el servicio es destruido
   */
  ngOnDestroy(): void {
    this.eventNotificationSubject.complete();
  }

  private limpiarListeners(socket: Socket) {
    // Limpiar listeners previos para evitar duplicados si start() se llama más de una vez
    socket.off(EVENT_GATEWAY.CONNECT);
    socket.off(EVENT_GATEWAY.DISCONNECT);
    socket.off(EVENT_GATEWAY.MICA_CHAT_CHAT_ACTUALIZADO);
    socket.off(EVENT_GATEWAY.MICA_CHAT_MENSAJE_NUEVO);
    socket.off(EVENT_GATEWAY.MICA_CHAT_MENSAJE_ACTUALIZADO);
    socket.off(EVENT_GATEWAY.MICA_CHAT_CONEXION_ACTUALIZADA);
  }
}
